import { useGetAttendanceStatusesQuery } from "../../../../store/selectableSlice";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { useState, useEffect, useRef } from "react";
import CalendarComp from "../../../../components/Common/CalendarComp";
import { BsCalendar3 } from "react-icons/bs";
import styled from "styled-components";

export const status_icon = {
  1: "/Icon/tick-circle-success.svg",
  2: "/Icon/tick-circle-warning.svg",
  3: "/Icon/tick-circle-info.svg",
  4: "/Icon/tick-circle-danger.svg",
};

const AttendanceFilter = ({ onDateChange, showStatusLegend = true }) => {
  const { data: attendance_statuses = [] } = useGetAttendanceStatusesQuery();
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const calendarRef = useRef(null);

  // Extract year and month from selected date
  const selectedYear = selectedDate.getFullYear();
  const selectedMonth = selectedDate.getMonth() + 1;

  // Format selected date for display
  const formattedDate = selectedDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const handleDateSelect = (date) => {
    // Set to the first day of the selected month
    const newDate = new Date(date.getFullYear(), date.getMonth(), 1);
    setSelectedDate(newDate);
    setShowCalendar(false);
  };

  // Notify parent when date changes
  useEffect(() => {
    if (onDateChange) {
      onDateChange(selectedYear, selectedMonth);
    }
  }, [selectedYear, selectedMonth, onDateChange]);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    if (showCalendar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCalendar]);

  return (
    <FilterContainer className="shadow-sm">
      {showStatusLegend && (
      <StatusLegend>
        <div className="d-flex align-items-center gap-2 mb-3">
          <div style={{ width: "4px", height: "16px", background: "var(--color-primary)", borderRadius: "2px" }}></div>
          <h6 className="mb-0 fw-bold text-black1" style={{ fontSize: "15px" }}>Attendance Status Guide</h6>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {attendance_statuses.map((status, index) => {
            return (
              <StatusBadge key={index}>
                <FallbackImage
                  src={status_icon[status?.id]}
                  width="18px"
                  height="18px"
                />
                <span>{status?.name}</span>
              </StatusBadge>
            );
          })}
        </div>
      </StatusLegend>
      )}

      <DatePickerContainer ref={calendarRef}>
        <div className="d-flex flex-column align-items-md-end gap-2">
          <span className="text-muted small fw-medium">Select Period</span>
          <DatePickerButton
            onClick={() => setShowCalendar(!showCalendar)}
            className="d-flex align-items-center gap-2"
          >
            <div className="d-flex align-items-center gap-2">
              <BsCalendar3 size={16} className="text-primary1" />
              <span className="fw-bold">{formattedDate}</span>
            </div>
            <span className="ms-2" style={{ fontSize: "10px", opacity: 0.5 }}>
              ▼
            </span>
          </DatePickerButton>
        </div>

        {showCalendar && (
          <CalendarDropdown>
            <CalendarComp
              value={selectedDate}
              onChange={handleDateSelect}
              maxDate={new Date()}
            />
          </CalendarDropdown>
        )}
      </DatePickerContainer>
    </FilterContainer>
  );
};

const FilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 1.25rem 1.5rem;
  background: #ffffff;
  border: 1px solid #f1f1f1;
  border-radius: 15px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const StatusLegend = styled.div`
  flex: 1;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #555;
  transition: all 0.2s ease;

  &:hover {
    background: white;
    border-color: #ddd;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const DatePickerContainer = styled.div`
  position: relative;
  z-index: 10;
`;

const DatePickerButton = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 0.6rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #333;
  font-size: 14px;
  min-width: 200px;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0,0,0,0.02);

  &:hover {
    border-color: var(--color-primary);
    background: #fff;
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
  }

  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
`;

const CalendarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding: 1rem;
  z-index: 1000;
  animation: slideDown 0.2s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    right: auto;
    left: 0;
  }
`;

export default AttendanceFilter;
